// Router
import { useNavigate, useParams } from "@/lib/router"
import { Routes } from "@/constants/routes"

// Queries
import { ProjectTypeEnum, useSingleProjectStatsQuery } from "@/api/graphql"

// UI
import { Card } from "@/components/Card"
import { InternalStateOfReturnGraph } from "../_components/InternalStateOfReturnGraph"
import { ProjectInterestPeriodsTable } from "./_components/ProjectInterestPeriodsTable"
import { PageTemplate } from "@/layouts/PageTemplate"
import { ProjectSelector } from "@/components/ProjectSelector"
import { GraphLabel, GraphLabels } from "@/components/graphs/GraphLabels"
import { Heading } from "@/components/Typography"
import { SolarInterestTariffGraph } from "@/components/graphs/types/area/SolarInterestTariffGraph"
import { SolarInterestTariffDownloads } from "@/pages/finance/solar-interest-tariff/_components/SolarInterestTariffDownloads"

// Components
import { SingleProjectStatsInternalRateOfReturn } from "../_components/SingleProjectStatsInternalRateOfReturn"

// I18n
import { useTrans } from "@/i18n"

// Context
import { useFeatureFlags } from "@/context/user"
import { AuthStateStatusEnum, useAuth } from "@/context/auth"

// Graphs
import { GlobalSolarInterestTariffGraph } from "./_components/GlobalSolarInterestTariffGraph"

// Contants
const ENABLE_SOLAR_TARIFF_GRAPH = false
const ENABLE_IRR_GRAPH = false

/**
 * SingleProjectStats
 * @param param0
 * @returns
 */
export function SingleProjectStats() {
	// I18n
	const t = useTrans("dashboard")

	// Context
	const { getFeatureFlagValue } = useFeatureFlags()
	const auth = useAuth()

	// Router
	const navigate = useNavigate()
	const params = useParams()
	const projectId = Number(params.id)

	// Fetch
	const { data } = useSingleProjectStatsQuery({
		id: String(params.id),
	})

	return (
		<div className="my-5">
			<PageTemplate
				title={data?.project?.name || ""}
				topRightComponent={
					<ProjectSelector
						projectId={projectId}
						onChange={(id) =>
							navigate(
								Routes.StatisticsPlatformSingleProject.replace(
									":id",
									id,
								),
							)
						}
					/>
				}
				backHref={Routes.StatisticsProjects}
			>
				<div className="grid grid-cols-1 gap-8 md:col-span-4 lg:grid-cols-2 xl:col-span-3">
					{data?.project?.type === ProjectTypeEnum.Portfolio &&
						getFeatureFlagValue("ENABLE_DISPLAY_IRR") === true && (
							<SingleProjectStatsInternalRateOfReturn
								projectId={projectId}
							/>
						)}

					<Card className="h-full group-hover:bg-gray-50 md:col-span-1 xl:col-span-1">
						{/** Project type */}
						<div>
							{t(
								"dashboard.stats.project_stats.info.project_type",
							)}
							:{" "}
							<strong>
								{t(
									`common:common.project.type.${data?.project?.type}`,
								)}
							</strong>
						</div>

						{/** Project interest_type */}
						<div>
							{t(
								"dashboard.stats.project_stats.info.interest_type",
							)}
							:{" "}
							<strong>
								{data?.project
									?.interest_payment_calculation_type &&
									t(
										`dashboard.stats.project_stats.interest_calculation_types.${
											data.project
												.interest_payment_calculation_type
										}`,
									)}
							</strong>
						</div>

						{/* Project start */}
						<div>
							{t("dashboard.stats.project_stats.info.start_date")}
							: <strong>{data?.project?.start}</strong>
						</div>

						{/** Energy supplier */}
						{data?.project?.supplier ? (
							<div>
								{t(
									"dashboard.stats.project_stats.info.energy_supplier",
								)}
								:{" "}
								<strong>{data?.project?.supplier?.name}</strong>
							</div>
						) : null}

						<div>
							{t(
								"dashboard.stats.project_stats.info.financial_structure",
							)}
							: <b>{data?.project?.amortization_schedule}</b>
						</div>

						{/** CAP / FLOOR */}
						{data?.project?.cap_eur_interest_per_share &&
						data?.project?.floor_eur_interest_per_share ? (
							<div>
								{t(
									"dashboard.stats.project_stats.info.cap_floor",
								)}
								:{" "}
								<b>
									&euro;{" "}
									{data?.project?.cap_eur_interest_per_share}/
									&euro;{" "}
									{
										data?.project
											?.floor_eur_interest_per_share
									}
								</b>
								<br />
							</div>
						) : null}

						{/** Maturity */}
						<div>
							{t("dashboard.stats.project_stats.info.maturity")}:{" "}
							<b>
								{data?.project?.duration}{" "}
								{t("dashboard.stats.project_stats.info.years")}
							</b>
						</div>
					</Card>
				</div>

				{/** Show graph when project type is solarShares */}
				{ENABLE_IRR_GRAPH ? <InternalStateOfReturnGraph /> : null}

				{/** Interest periods table */}
				<ProjectInterestPeriodsTable />

				{/* For SolarShares projects show interest tariffs*/}
				{data?.project?.type === ProjectTypeEnum.SolarShares ? (
					<>
						{auth.status === AuthStateStatusEnum.LOGGED_IN ? (
							<SolarInterestTariffDownloads />
						) : null}

						{/** Endex graph */}
						{/** @ts-expect-error Temp var */}
						{ENABLE_SOLAR_TARIFF_GRAPH === true ? (
							<Card>
								<Heading
									as="h3"
									styleAs="h6"
									className="mr-2 lg:mr-0"
								>
									{t(
										"finance:finance.solar_interest_tariff.block.endex_tariffs_full_year.title",
									)}
								</Heading>

								<SolarInterestTariffGraph>
									<SolarInterestTariffGraph.Content>
										<GlobalSolarInterestTariffGraph />
									</SolarInterestTariffGraph.Content>
									<SolarInterestTariffGraph.Footer>
										<GraphLabels className="p-3 px-4 sm:p-5 sm:px-6">
											<GraphLabel
												type="line"
												variant="primary"
												title={t(
													"finance:finance.solar_interest_tariff.block.endex_tariffs",
												)}
											/>
										</GraphLabels>
									</SolarInterestTariffGraph.Footer>
								</SolarInterestTariffGraph>
							</Card>
						) : null}
					</>
				) : null}
			</PageTemplate>
		</div>
	)
}
